import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useRef, useState } from 'react';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import type { ActiveFeaturedExpertItem } from '@/services/getFeaturedExpertsV2';

/**
 * Maximum number of viewed expert keys to retain in storage.
 * Prevents unbounded growth of the stored keys array.
 */
const MAX_VIEWED_KEYS = 100;

/**
 * Type guard to validate that parsed JSON is a string array.
 */
const isStringArray = (value: unknown): value is string[] => {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
};

/**
 * Generates a unique key for a featured expert based on expert ID and cancer type.
 * This ensures we track the specific expert-cancer type combination.
 */
const getFeaturedExpertKey = (item: ActiveFeaturedExpertItem): string => {
  return `${item.expert.id}-${item.cancerType.id}`;
};

/**
 * Hook for tracking whether there are new (unviewed) featured experts.
 *
 * Uses AsyncStorage to persist which featured experts have been viewed.
 * Compares current featured experts against viewed list to determine
 * if there are new experts to show an indicator for.
 *
 * @param featuredExperts - Current list of featured experts from API
 * @returns Object with:
 *   - hasNewExperts: boolean indicating if there are unviewed featured experts
 *   - markAsViewed: function to call when user views the Featured tab
 *   - isLoading: boolean indicating if storage is still being read
 *   - error: Error object if storage operations fail
 */
export function useFeaturedExpertsIndicator(featuredExperts: ActiveFeaturedExpertItem[]) {
  const [viewedKeys, setViewedKeys] = useState<Set<string>>(new Set());

  const [isLoading, setIsLoading] = useState(true);

  const [error, setError] = useState<Error | null>(null);

  const writingRef = useRef(false);

  useEffect(() => {
    const loadViewedExperts = async () => {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEYS.DIRECTORY_FEATURED_EXPERTS_VIEWED);

        if (stored) {
          const parsed = JSON.parse(stored);

          if (isStringArray(parsed)) {
            setViewedKeys(new Set(parsed));
          } else {
            console.warn('Invalid stored viewed experts format, expected string array');
          }
        }
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Unknown error');

        console.error('Failed to load viewed featured experts:', errorObj);

        setError(errorObj);
      } finally {
        setIsLoading(false);
      }
    };

    void loadViewedExperts();
  }, []);

  const hasNewExperts =
    !isLoading &&
    featuredExperts.length > 0 &&
    featuredExperts.some((item) => !viewedKeys.has(getFeaturedExpertKey(item)));

  const markAsViewed = useCallback(async () => {
    if (featuredExperts.length === 0) {
      return;
    }

    if (writingRef.current) {
      return;
    }

    writingRef.current = true;

    try {
      const currentKeys = featuredExperts.map(getFeaturedExpertKey);

      setViewedKeys((prevKeys) => {
        const mergedKeys = new Set([...prevKeys, ...currentKeys]);

        const keysArray = Array.from(mergedKeys).slice(-MAX_VIEWED_KEYS);

        AsyncStorage.setItem(
          STORAGE_KEYS.DIRECTORY_FEATURED_EXPERTS_VIEWED,
          JSON.stringify(keysArray)
        ).catch((err) => {
          const errorObj = err instanceof Error ? err : new Error('Unknown error');

          console.error('Failed to save viewed featured experts:', errorObj);

          setError(errorObj);
        });

        return new Set(keysArray);
      });
    } finally {
      writingRef.current = false;
    }
  }, [featuredExperts]);

  return {
    hasNewExperts,
    markAsViewed,
    isLoading,
    error,
  };
}
